import { Faq, NavItem } from '../types/content'

export const navItems: NavItem[] = [
  { label: 'Symptoms', to: '/symptoms' },
  { label: 'Care', to: '/care' },
  { label: 'Library', to: '/library' },
  { label: 'Circles', to: '/circles' },
  { label: 'Treatments', to: '/treatments' },
  { label: 'Testing', to: '/testing' },
  { label: 'Pricing', to: '/pricing' },
]

/** What Vesta refuses to do. Stated as commitments, not as marketing. */
export const commitments = [
  {
    title: 'We answer before we sell',
    detail:
      'The symptom decoder, the library and the stage guide are free and ungated. No email, no account, no “unlock your results”. Withholding an answer from this audience is the exact harm we exist to fix.',
  },
  {
    title: 'We say when we don’t know',
    detail:
      'Every education page carries a section on what the evidence does not settle. If a treatment is off-label, compounded, or thinly evidenced, that appears on the page — not in a footnote.',
  },
  {
    title: 'We won’t sell you a body',
    detail:
      'No before-and-after photographs. No goal weights. No anti-ageing language. Your body is changing because of a physiological transition, not because you failed at something.',
  },
  {
    title: 'You can leave whenever',
    detail:
      'Cancel in two clicks from your account. No retention call, no exit survey you cannot skip, no offer that appears only when you try to go.',
  },
]

export const steps = [
  {
    title: 'Say the one thing',
    detail:
      'Start with whatever is bothering you most. You do not need a symptom diary, a diagnosis, or the right vocabulary. Five questions, one at a time, and you can stop and come back.',
  },
  {
    title: 'A clinician reads it',
    detail:
      'A Menopause Society–certified clinician reviews your answers before your consult, so you are not explaining from scratch. Forty-five minutes for a first appointment, by video or phone.',
  },
  {
    title: 'Test only what changes the plan',
    detail:
      'Thyroid, ferritin, vitamin D, HbA1c and lipids — the things that mimic perimenopause and are treatable. We will explain why we are not diagnosing you from a hormone panel.',
  },
  {
    title: 'Start, then adjust',
    detail:
      'Treatment titrated to how you feel at six and twelve weeks. Message your clinician between appointments without paying for the privilege.',
  },
]

export const pricingTiers = [
  {
    name: 'Free',
    price: 0,
    cadence: '',
    intro: 'Everything we know, available to anyone, with nothing asked in return.',
    features: [
      'The full symptom decoder',
      'Your questions-to-ask summary, copyable',
      'The entire library, unpaywalled',
      'The 3am thread',
    ],
    cta: 'Use the decoder',
    to: '/symptoms',
    featured: false,
  },
  {
    name: 'Membership',
    price: 45,
    cadence: 'per month',
    intro: 'Clinical care with a named clinician, plus every circle.',
    features: [
      'A 45-minute first consult',
      'Unlimited messaging with your care team',
      'All weekly circles and clinician Q&As',
      'Baseline screening panel included',
      'Prescriptions written where appropriate',
    ],
    cta: 'Start with one question',
    to: '/start',
    featured: true,
  },
  {
    name: 'Membership + Care Path',
    price: 119,
    cadence: 'per month',
    intro: 'Everything in Membership, plus medication and follow-up on a defined path.',
    features: [
      'Everything in Membership',
      'Medication included, shipped or sent to your pharmacy',
      'Titration reviews at 6 and 12 weeks',
      'Quarterly markers relevant to your path',
      'Price held for twelve months',
    ],
    cta: 'See the care paths',
    to: '/care',
    featured: false,
  },
]

export const labPanels = [
  {
    name: 'Baseline screen',
    markers: 14,
    price: 89,
    detail:
      'What we actually run at intake: thyroid, ferritin, vitamin D, HbA1c, lipids, liver and kidney function. Designed to catch the conditions that mimic perimenopause and are directly treatable.',
  },
  {
    name: 'Long-view panel',
    markers: 22,
    price: 149,
    detail:
      'The baseline plus ApoB, Lp(a), fasting insulin and inflammatory markers. For women thinking about the thirty years after the transition rather than the twelve months in front of them.',
  },
  {
    name: 'Under-45 assessment',
    markers: 9,
    price: 99,
    detail:
      'Where premature ovarian insufficiency is a genuine possibility, FSH and estradiol on two occasions plus AMH do carry information. This is the one situation where hormone testing earns its place.',
  },
]

export const faqs: Faq[] = [
  {
    question: 'Am I too young for this?',
    answer:
      'Almost certainly not. Perimenopause commonly begins in the early forties and can start in the mid-thirties. We assess your cycle history and symptoms rather than deciding by your birth year. Being told you are too young is the single most common reason women arrive here.',
  },
  {
    question: 'Is hormone therapy safe?',
    answer:
      'For most women who are candidates, yes — and the risk is smaller than two decades of headlines suggested. The 2002 study behind those headlines used a different formulation in women who were on average 63. We will go through your personal risk in absolute numbers, and if hormone therapy is not right for you, we will tell you exactly why and what we would do instead.',
  },
  {
    question: 'Will you test my hormones?',
    answer:
      'Usually not, and we would rather explain why than run a panel to look thorough. In perimenopause, hormone levels swing so widely that a single draw reflects that morning and little else. Diagnosis is clinical. We do test thyroid, ferritin, vitamin D, HbA1c and lipids, because those mimic perimenopause and are treatable.',
  },
  {
    question: 'Do I have to pay to use the symptom decoder?',
    answer:
      'No. It is free, requires no email address and no account, and your summary is yours to take to any clinician — including one who is not us. The library is unpaywalled for the same reason.',
  },
  {
    question: 'I have had breast cancer. Is there anything for me?',
    answer:
      'Yes, and this is where women are most often turned away at the door. Non-hormonal options including fezolinetant and low-dose SSRIs treat vasomotor symptoms effectively. Major oncology and menopause bodies now support low-dose vaginal estrogen for many survivors after discussion with their oncology team. We will coordinate rather than refuse.',
  },
  {
    question: 'What if I have already been dismissed by several doctors?',
    answer:
      'That is the norm here, not the exception. Bring whatever you have — three months of cycle lengths is the single most useful thing. Our clinicians are Menopause Society certified, which means they were specifically trained in this rather than covering it in an afternoon.',
  },
  {
    question: 'Can I cancel?',
    answer:
      'In two clicks, from your account, at any time. No retention call, no exit survey, and no discount that only appears when you try to leave.',
  },
]

export const clinicians = [
  {
    name: 'Dr Imogen Halvorsen',
    role: 'MD, Menopause Society Certified Practitioner',
    detail: 'Fourteen years in women’s health. Leads hormone therapy assessment and the monthly Q&A.',
  },
  {
    name: 'Dr Ruth Adeyemi',
    role: 'MD, pelvic health',
    detail: 'Genitourinary syndrome of menopause, painful sex, recurrent UTI prevention.',
  },
  {
    name: 'Nadia Okonkwo',
    role: 'Women’s health nurse practitioner',
    detail: 'Early signals and under-45 assessment. Facilitates the Too-Young Circle.',
  },
  {
    name: 'Dr Priya Raman',
    role: 'PhD, metabolic health and nutrition',
    detail: 'Midlife metabolism, strength programming, and the evidence review on our library.',
  },
]
